import { Search, X, Layers } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { ALL_BRAND, ALL_BRAND_LABEL } from '@/lib/api';
import type { AvailablePeriods, BrandStat, ReportStatus } from '@/lib/api';

const ALL = '__all__';

export interface ReportsFilterState {
  brand: string;
  period: string;
  status: ReportStatus | '';
  search: string;
}

export interface ReportsFilterBarProps {
  state: ReportsFilterState;
  brands: BrandStat[];
  available: AvailablePeriods | null;
  onChange: (patch: Partial<ReportsFilterState>) => void;
  onReset: () => void;
}

function formatMonth(month: string): string {
  const [year, mo] = month.split('-');
  return `T${parseInt(mo!, 10)}/${year}`;
}

function formatQuarter(quarter: string): string {
  const [year, q] = quarter.split('-Q');
  return `${year} · Q${q}`;
}

export function ReportsFilterBar({ state, brands, available, onChange, onReset }: ReportsFilterBarProps) {
  const hasActive = state.brand || state.period || state.status || state.search;

  return (
    <div className="flex flex-col gap-2 rounded-lg border bg-card p-3 shadow-sm sm:flex-row sm:items-center">
      {/* Search */}
      <div className="relative flex-1 sm:max-w-xs">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={state.search}
          onChange={(e) => onChange({ search: e.target.value })}
          placeholder="Tìm tiêu đề báo cáo..."
          className="h-9 pl-9 text-xs"
          autoComplete="off"
        />
      </div>

      <Select
        value={state.brand || ALL}
        onValueChange={(v) => onChange({ brand: v === ALL ? '' : v })}
      >
        <SelectTrigger className="h-9 w-full text-xs sm:w-[180px]">
          <SelectValue placeholder="Tất cả hãng" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>Tất cả hãng</SelectItem>
          <SelectItem value={ALL_BRAND}>
            <span className="flex items-center gap-1.5">
              <Layers className="h-3 w-3 text-primary" />
              {ALL_BRAND_LABEL}
            </span>
          </SelectItem>
          {brands.map((b) => (
            <SelectItem key={b.brand} value={b.brand}>
              {b.brand} <span className="ml-1 text-muted-foreground">({b.count})</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={state.period || ALL}
        onValueChange={(v) => onChange({ period: v === ALL ? '' : v })}
      >
        <SelectTrigger className="h-9 w-full text-xs sm:w-[180px]">
          <SelectValue placeholder="Tất cả period" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>Tất cả period</SelectItem>
          {available?.quarters.length ? (
            <>
              <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Theo Quý
              </div>
              {available.quarters.map((q) => (
                <SelectItem key={q} value={q} className="text-xs">
                  {formatQuarter(q)}
                </SelectItem>
              ))}
            </>
          ) : null}
          {available?.months.length ? (
            <>
              <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Theo Tháng
              </div>
              {available.months.map((m) => (
                <SelectItem key={m} value={m} className="text-xs">
                  {formatMonth(m)}
                </SelectItem>
              ))}
            </>
          ) : null}
        </SelectContent>
      </Select>

      <Select
        value={state.status || ALL}
        onValueChange={(v) => onChange({ status: v === ALL ? '' : (v as ReportStatus) })}
      >
        <SelectTrigger className="h-9 w-full text-xs sm:w-[140px]">
          <SelectValue placeholder="Mọi status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>Mọi status</SelectItem>
          <SelectItem value="draft">Draft</SelectItem>
          <SelectItem value="published">Published</SelectItem>
          <SelectItem value="archived">Archived</SelectItem>
        </SelectContent>
      </Select>

      {hasActive && (
        <Button variant="ghost" size="sm" onClick={onReset} className="text-muted-foreground">
          <X className="h-3.5 w-3.5" />
          Reset
        </Button>
      )}
    </div>
  );
}
